/**
 * Whether a drag carries files from outside the browser, as opposed to a card being dragged across
 * a board. Every handler asks this first: a ticket drag must reach the board's own drag and drop
 * untouched, and it does that by being left alone here.
 *
 * @param dataTransfer - The event's transfer, absent on some synthetic events.
 * @returns True when the platform announced files on the drag.
 */
export function isFileDrag(dataTransfer: DataTransfer | null) {
	// `types` is read rather than copied: this runs on every dragover the document sees, and an
	// array per frame per drag is garbage nobody needs.
	return dataTransfer?.types.includes("Files") === true;
}

/**
 * The files a drop carried, folders left behind.
 *
 * `DataTransfer.files` reports a dropped folder as one entry the server would store as an empty
 * file, so the items are walked instead and whatever the platform calls a directory is dropped.
 * Expanding one is a recursive `webkitGetAsEntry` walk this surface has no use for: what gets
 * dragged onto a ticket is the photograph or the delivery note somebody just received.
 *
 * `webkitGetAsEntry` has to be called while the event is still being handled, because the items
 * list is emptied the moment the handler returns.
 *
 * @param dataTransfer - The drop event's transfer.
 * @returns The dropped files, in the order the platform listed them.
 */
export function collectDroppedFiles(dataTransfer: DataTransfer | null): File[] {
	if (!dataTransfer) return [];
	const items = Array.from(dataTransfer.items ?? []);
	if (!items.length) return Array.from(dataTransfer.files ?? []);
	const files: File[] = [];
	for (const item of items) {
		if (item.kind !== "file") continue;
		if (item.webkitGetAsEntry?.()?.isDirectory) continue;
		const file = item.getAsFile();
		if (file) files.push(file);
	}
	return files;
}
